// Scale bridge dashboard - the at-a-glance screen.
//
// Reads top to bottom in the order somebody actually wants it: the four numbers
// that describe the day, anything wrong, then the shape of the day and who is
// reporting, then the last few weighings.
//
// ─── The recent list opens the same drawer the table does ──────────────
// It used to be inert. An operator who spotted an odd weight here had to go to
// Weighings, find the same row again and click that one - for a record that was
// already under the cursor. Same component, same data, same gesture.

import { useState } from 'react';
import { Alert, Button, Card, Col, Empty, List, Row, Skeleton, Space, Tag, Tooltip, Typography } from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

import KpiStrip from '../components/KpiStrip';
import StatusTag from '../components/StatusTag';
import WeightByHourChart from '../components/WeightByHourChart';
import ReadingDetailDrawer from '../components/ReadingDetailDrawer';
import { isStale, orDash, relative, timestamp, weightKg } from '../lib/format';
import { selectWeightByHour, useReadingsStore } from '../state/readingsStore';
import { useAuthStore } from '../state/authStore';
import type { Reading } from '../types';

const { Text } = Typography;

const RECENT_COUNT = 8;

export default function Overview() {
    const navigate = useNavigate();
    const session = useAuthStore((s) => s.session)!;
    const readings = useReadingsStore((s) => s.readings);
    const stats = useReadingsStore((s) => s.stats);
    const loaded = useReadingsStore((s) => s.loaded);

    const [selected, setSelected] = useState<Reading | null>(null);

    const byHour = selectWeightByHour(readings);
    const recent = readings.slice(0, RECENT_COUNT);

    return (
        <>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <KpiStrip stats={stats} loading={!loaded} />

                {/* Raised where it will be seen rather than left to be noticed in a
                    table: unparsed weights mean labels are arriving that the parser
                    does not fully understand, which is how a format change announces
                    itself. */}
                {stats && stats.unparsedWeightCount > 0 && (
                    <Alert
                        type="warning"
                        showIcon
                        message={`${stats.unparsedWeightCount} weighing${stats.unparsedWeightCount === 1 ? '' : 's'} recorded without a readable weight`}
                        description="The label arrived but its total could not be parsed as a number. Open the weighing and check the raw label lines — the printer's wording may have changed."
                        action={
                            <Button size="small" type="primary" ghost onClick={() => navigate('/readings')}>
                                View weighings
                            </Button>
                        }
                    />
                )}

                {/* align="stretch" so the two cards end level. Ant Design's Row
                    aligns to the top by default, which left the shorter card
                    floating with a ragged shelf of page under it. */}
                <Row gutter={[16, 16]} align="stretch">
                    <Col xs={24} xl={15}>
                        <Card
                            // The title names the series, which is why the chart carries
                            // no legend - there is only one thing plotted.
                            //
                            // It is a plain string: Ant Design clips a card title to one
                            // line with an ellipsis, so the sentence underneath it has to
                            // live in the body rather than be smuggled into the header.
                            title="Net weight recorded today"
                            style={{ height: '100%' }}
                        >
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                Total kilograms per hour. Hover a bar for the number of weighings.
                            </Text>
                            <div style={{ marginTop: 12 }}>
                                {loaded ? (
                                    <WeightByHourChart data={byHour} />
                                ) : (
                                    <Skeleton active paragraph={{ rows: 6 }} title={false} />
                                )}
                            </div>
                        </Card>
                    </Col>

                    <Col xs={24} xl={9}>
                        <Card
                            title="Devices reporting"
                            extra={
                                <Button type="link" size="small" onClick={() => navigate('/devices')}>
                                    Manage
                                </Button>
                            }
                            style={{ height: '100%' }}
                        >
                            {!loaded ? (
                                <Skeleton active paragraph={{ rows: 4 }} title={false} />
                            ) : !stats || stats.devices.length === 0 ? (
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description="No device has reported yet"
                                    style={{ paddingBlock: 40 }}
                                />
                            ) : (
                                <List
                                    size="small"
                                    dataSource={stats.devices}
                                    renderItem={(d) => (
                                        <List.Item>
                                            <List.Item.Meta
                                                title={
                                                    <span className="mono" style={{ fontSize: 13 }}>
                                                        {d.deviceId}
                                                    </span>
                                                }
                                                description={
                                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                                        <span className="num">{d.count.toLocaleString('en-IN')}</span>{' '}
                                                        weighing{d.count === 1 ? '' : 's'} · last {relative(d.lastSeenAt)}
                                                    </Text>
                                                }
                                            />
                                            {/* "Quiet" rather than "offline": nothing here can
                                                tell a switched-off bridge from a shift with no
                                                weighings, and claiming the stronger fact would
                                                send somebody to check a machine that is fine. */}
                                            <Tooltip
                                                title={
                                                    isStale(d.lastSeenAt)
                                                        ? `Nothing since ${timestamp(d.lastSeenAt)}. That may be a quiet shift rather than a fault.`
                                                        : `Last weighing ${timestamp(d.lastSeenAt)}`
                                                }
                                            >
                                                <Tag
                                                    color={isStale(d.lastSeenAt) ? 'default' : 'success'}
                                                    style={{ marginInlineEnd: 0 }}
                                                >
                                                    {isStale(d.lastSeenAt) ? 'Quiet' : 'Active'}
                                                </Tag>
                                            </Tooltip>
                                        </List.Item>
                                    )}
                                />
                            )}
                        </Card>
                    </Col>
                </Row>

                <Card
                    title="Latest weighings"
                    extra={
                        <Button type="link" size="small" onClick={() => navigate('/readings')}>
                            See all <ArrowRightOutlined />
                        </Button>
                    }
                >
                    {!loaded ? (
                        <Skeleton active paragraph={{ rows: 5 }} title={false} />
                    ) : recent.length === 0 ? (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={
                                <Space direction="vertical" size={2}>
                                    <Text type="secondary">Nothing recorded yet</Text>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        Print a label on the weighbridge and it will appear here.
                                    </Text>
                                </Space>
                            }
                            style={{ paddingBlock: 24 }}
                        />
                    ) : (
                        <List
                            dataSource={recent}
                            renderItem={(r) => (
                                <List.Item
                                    className="sb-row-link"
                                    onClick={() => setSelected(r)}
                                    // Reachable and operable from the keyboard, because a
                                    // row that opens a drawer is a control whatever element
                                    // it is built from.
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            setSelected(r);
                                        }
                                    }}
                                >
                                    <List.Item.Meta
                                        title={
                                            <Space size={8}>
                                                <span className="num" style={{ fontWeight: 650 }}>
                                                    {weightKg(r.totalNetWeightKg)}
                                                </span>
                                                <StatusTag value={r.testing} />
                                            </Space>
                                        }
                                        description={
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                <span className="num">{timestamp(r.receivedAt)}</span> · batch{' '}
                                                {orDash(r.batchNo)} ·{' '}
                                                <span className="mono">{orDash(r.barcode)}</span>
                                            </Text>
                                        }
                                    />
                                    <Text type="secondary" className="num" style={{ fontSize: 12 }}>
                                        {r.items.length} item{r.items.length === 1 ? '' : 's'}
                                    </Text>
                                </List.Item>
                            )}
                        />
                    )}
                </Card>
            </Space>

            <ReadingDetailDrawer
                reading={selected}
                session={session}
                onClose={() => setSelected(null)}
            />
        </>
    );
}
